/**
 * Application-wide Constants & Enums
 * Central place for all magic strings and configuration values
 */

// ─── User Roles ───
export const ROLES = {
  SUPER_ADMIN: 'superAdmin',
  ADMIN: 'admin',
  TEACHER: 'teacher',
};

// ─── Exam Types ───
export const EXAM_TYPES = {
  HALF_YEARLY: 'halfYearly',
  ANNUAL: 'annual',
  MODEL_TEST: 'modelTest',
  SPECIAL: 'special',
};

export const EXAM_TYPE_LABELS = {
  [EXAM_TYPES.HALF_YEARLY]: 'Half Yearly',
  [EXAM_TYPES.ANNUAL]: 'Annual',
  [EXAM_TYPES.MODEL_TEST]: 'Model Test',
  [EXAM_TYPES.SPECIAL]: 'Special',
};

// ─── Exam Session Status ───
export const EXAM_STATUS = {
  DRAFT: 'draft',
  ACTIVE: 'active',
  COMPLETED: 'completed',
  ARCHIVED: 'archived',
};

// ─── Mark Status ───
export const MARK_STATUS = {
  DRAFT: 'draft',
  SUBMITTED: 'submitted',
  FROZEN: 'frozen',
  UNLOCKED: 'unlocked',           // Admin approved one-time edit
  FINAL_LOCKED: 'finalLocked',    // After one-time edit, permanently locked
  UPDATE_REQUESTED: 'updateRequested',
  UPDATE_APPROVED: 'updateApproved',
};

// ─── Enrollment/Participant Status ───
export const ENROLLMENT_STATUS = {
  ELIGIBLE: 'eligible',
  LATE_ADMISSION: 'lateAdmission',
  WITHDRAWN: 'withdrawn',
  ABSENT: 'absent',
  DISQUALIFIED: 'disqualified',
};

// ─── Participant Status (Deprecated, use ENROLLMENT_STATUS) ───
export const PARTICIPANT_STATUS = {
  ACTIVE: 'eligible', // Map old active to eligible
  WITHDRAWN: 'withdrawn',
};

// ─── Result Status ───
export const RESULT_STATUS = {
  PASSED: 'passed',
  FAILED: 'failed',
  WITHHELD: 'withheld',
};

// ─── Subject Types ───
export const SUBJECT_TYPES = {
  COMPULSORY: 'compulsory',
  OPTIONAL: 'optional',
  ELECTIVE: 'elective',
};

// ─── Update Request Status ───
export const UPDATE_REQUEST_STATUS = {
  PENDING: 'pending',
  APPROVED: 'approved',
  REJECTED: 'rejected',
};

// ─── Unlock Request Status ───
export const UNLOCK_REQUEST_STATUS = {
  PENDING: 'pending',
  APPROVED: 'approved',
  REJECTED: 'rejected',
  USED: 'used',       // Teacher has used the one-time edit
};

// ─── Audit Actions ───
export const AUDIT_ACTIONS = {
  CREATE: 'CREATE',
  UPDATE: 'UPDATE',
  DELETE: 'DELETE',
  PUBLISH: 'PUBLISH',
  IMPORT: 'IMPORT',
  CALCULATE: 'CALCULATE',
  // Mark Entry Actions
  DRAFT_SAVED: 'DRAFT_SAVED',
  DRAFT_RESTORED: 'DRAFT_RESTORED',
  MARKS_SUBMITTED: 'MARKS_SUBMITTED',
  MARKS_UPDATED: 'MARKS_UPDATED',
  MARKED_ABSENT: 'MARKED_ABSENT',
  // Unlock Workflow Actions
  UNLOCK_REQUESTED: 'UNLOCK_REQUESTED',
  UNLOCK_APPROVED: 'UNLOCK_APPROVED',
  UNLOCK_REJECTED: 'UNLOCK_REJECTED',
  // Admin Actions
  DEADLINE_OVERRIDE: 'DEADLINE_OVERRIDE',
  ADMIN_MODIFICATION: 'ADMIN_MODIFICATION',
  RESULT_REOPENED: 'RESULT_REOPENED',
};

// ─── Notification Types ───
export const NOTIFICATION_TYPES = {
  NOTICE: 'NOTICE',
  MARK_SUBMISSION_REMINDER: 'MARK_SUBMISSION_REMINDER',
  MARK_SUBMITTED: 'MARK_SUBMITTED',
  MARK_UNLOCK_REQUEST: 'MARK_UNLOCK_REQUEST',
  MARK_UNLOCK_APPROVED: 'MARK_UNLOCK_APPROVED',
  MARK_UNLOCK_REJECTED: 'MARK_UNLOCK_REJECTED',
  RESULT_PUBLISHED: 'RESULT_PUBLISHED',
  SYSTEM: 'SYSTEM',
  WARNING: 'WARNING',
  SUCCESS: 'SUCCESS',
  INFO: 'INFO'
};

// ─── Notification Priorities ───
export const NOTIFICATION_PRIORITY = {
  LOW: 'low',
  MEDIUM: 'medium',
  HIGH: 'high',
  URGENT: 'urgent'
};

// ─── Notice Board Status ───
export const NOTICE_STATUS = {
  DRAFT: 'draft',
  PUBLISHED: 'published',
  ARCHIVED: 'archived'
};

// ─── Target Audience ───
export const NOTIFICATION_AUDIENCE = {
  ALL: 'all',
  TEACHERS: 'teachers',
  ADMINS: 'admins',
  SELECTED_USERS: 'selected'
};

// ─── Collection Names (Exam System Firestore) ───
export const COLLECTIONS = {
  SUBJECTS: 'subjects',
  CLASS_SUBJECTS: 'classSubjects',
  EXAM_TEACHERS: 'examTeachers',
  TEACHER_ASSIGNMENTS: 'teacherAssignments',
  EXAM_PARTICIPANTS: 'examParticipants',
  EXAM_SESSIONS: 'examSessions',
  EXAM_ENROLLMENTS: 'examEnrollments',
  MARKS: 'marks',
  RESULTS: 'results',
  RESULT_CALCULATIONS: 'resultCalculations',
  RESULT_PUBLISH_HISTORY: 'resultPublishHistory',
  GRADING_POLICIES: 'gradingPolicies',
  AUDIT_LOGS: 'auditLogs',
  NOTIFICATIONS: 'notifications',
  USER_NOTIFICATIONS: 'userNotifications',
  SYSTEM_CONFIG: 'systemConfig',
  MARK_UPDATE_REQUESTS: 'markUpdateRequests',
  ANALYTICS: 'analytics',
};

// ─── Core SIS Collection Names (READ ONLY) ───
export const CORE_SIS_COLLECTIONS = {
  STUDENTS: 'students',
  TEACHERS: 'teachers',
  ENROLLMENTS: 'enrollments',
  ACADEMIC_YEARS: 'academicYears',
  CLASSES: 'classes',
  SECTIONS: 'sections',
  USERS: 'users',
  SETTINGS: 'settings',
};

// ─── Default Values ───
export const DEFAULTS = {
  FULL_MARKS: 100,
  PASS_MARKS: 33,
  PAGE_SIZE: 25,
  STALE_TIME: 5 * 60 * 1000, // 5 minutes
  STALE_TIME_SHORT: 30 * 1000, // 30 seconds
};

// ─── Auto Save Configuration ───
export const AUTO_SAVE = {
  DEBOUNCE_MS: 4000,           // 4 seconds after last change
  PERIODIC_INTERVAL_MS: 30000, // 30 seconds periodic save
};

// ─── Setup Wizard Steps ───
export const SETUP_STEPS = [
  { id: 1, label: 'Academic Year', description: 'Set the current academic year' },
  { id: 2, label: 'Class', description: 'Configure classes' },
  { id: 3, label: 'Subject', description: 'Add subjects' },
  { id: 4, label: 'Class-Subject Mapping', description: 'Map subjects to classes' },
  { id: 5, label: 'Teacher Import', description: 'Import teachers from Core SIS' },
  { id: 6, label: 'Teacher Assignment', description: 'Assign teachers to subjects' },
  { id: 7, label: 'Grading Policy', description: 'Configure grading scale' },
];

// ─── Default Grading Scale ───
export const DEFAULT_GRADING_SCALE = [
  { grade: 'A+', gradePoint: 5.00, minMarks: 80, maxMarks: 100, remarks: 'Outstanding' },
  { grade: 'A',  gradePoint: 4.00, minMarks: 70, maxMarks: 79,  remarks: 'Excellent' },
  { grade: 'A-', gradePoint: 3.50, minMarks: 60, maxMarks: 69,  remarks: 'Very Good' },
  { grade: 'B',  gradePoint: 3.00, minMarks: 50, maxMarks: 59,  remarks: 'Good' },
  { grade: 'C',  gradePoint: 2.00, minMarks: 40, maxMarks: 49,  remarks: 'Satisfactory' },
  { grade: 'D',  gradePoint: 1.00, minMarks: 33, maxMarks: 39,  remarks: 'Pass' },
  { grade: 'F',  gradePoint: 0.00, minMarks: 0,  maxMarks: 32,  remarks: 'Fail' },
];
